import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";

export const createDefaultField = (type: SchemaField["type"], name = ""): SchemaField => {
  const id = Math.random().toString(36).slice(2, 9);
  const field: SchemaField = {
    id,
    name: name || `field_${Date.now().toString().slice(-4)}`,
    type,
    required: false,
    expanded: true,
    examples: [],
  };

  switch (type) {
    case "string":
      field.examples = ["Sample text"];
      break;
    case "number":
      field.examples = [42];
      break;
    case "boolean":
      field.examples = [true];
      break;
    case "date":
      field.examples = ["2024-01-15"];
      break;
    case "enum":
      field.enumValues = ["option1", "option2"];
      field.examples = ["option1"];
      break;
    case "array":
      field.arrayItemType = createDefaultField("string", "item");
      field.examples = [["item1", "item2"]];
      break;
    case "object":
      field.objectFields = [];
      field.examples = [{}];
      break;
  }

  return field;
};

export const findFieldById = (fields: SchemaField[], id: string): SchemaField | null => {
  for (const field of fields) {
    if (field.id === id) return field;
    if (field.objectFields) {
      const found = findFieldById(field.objectFields, id);
      if (found) return found;
    }
    if (field.arrayItemType?.id === id) return field.arrayItemType;
  }
  return null;
};

export const getDefaultExampleForType = (type: SchemaField["type"], field: SchemaField): any => {
  switch (type) {
    case "string":
      return "Sample text";
    case "number":
      return 42;
    case "boolean":
      return true;
    case "date":
      return "2024-01-15";
    case "enum":
      return field.enumValues?.[0] || "option1";
    case "array":
      return field.arrayItemType?.examples?.slice(0, 2) || ["item1", "item2"];
    case "object":
      const obj: any = {};
      field.objectFields?.forEach((objField) => {
        if (objField.examples && objField.examples.length > 0) {
          obj[objField.name] = objField.examples[0];
        }
      });
      return obj;
    default:
      return null;
  }
};

export const generateZodSchema = (fields: SchemaField[]): string => {
  const generateFieldSchema = (field: SchemaField): string => {
    let schema = "";

    switch (field.type) {
      case "string":
        schema = "z.string()";
        break;
      case "number":
        schema = "z.number()";
        break;
      case "boolean":
        schema = "z.boolean()";
        break;
      case "date":
        schema = "z.date()";
        break;
      case "enum":
        schema = `z.enum([${field.enumValues?.map((v) => `"${v}"`).join(", ") || ""}])`;
        break;
      case "array":
        if (field.arrayItemType) {
          schema = `z.array(${generateFieldSchema(field.arrayItemType)})`;
        } else {
          schema = "z.array(z.string())";
        }
        break;
      case "object":
        if (field.objectFields && field.objectFields.length > 0) {
          const objectFields = field.objectFields
            .map((f) => `    ${f.name}: ${generateFieldSchema(f)}`)
            .join(",\n");
          schema = `z.object({\n${objectFields}\n  })`;
        } else {
          schema = "z.object({})";
        }
        break;
    }

    if (!field.required) {
      schema += ".optional()";
    }

    if (field.description) {
      schema += `.describe("${field.description}")`;
    }

    return schema;
  };

  if (fields.length === 0) return "z.object({})";

  const schemaFields = fields
    .map((field) => `  ${field.name}: ${generateFieldSchema(field)}`)
    .join(",\n");
  return `z.object({\n${schemaFields}\n})`;
};
